import React from 'react';
import { Bell, TrendingUp, Newspaper, Clock, AlertCircle, ChevronRight, Check } from 'lucide-react';

const AlertListArtifact = ({ alerts }) => {
    if (!alerts || !Array.isArray(alerts) || alerts.length === 0) return null;

    const getCategoryIcon = (category) => {
        switch (category?.toLowerCase()) {
            case 'quantitative':
                return <TrendingUp size={16} className="text-blue-500" />;
            case 'content':
            case 'news':
                return <Newspaper size={16} className="text-orange-500" />;
            default:
                return <Bell size={16} className="text-purple-500" />;
        }
    };

    const formatCriteria = (criteria) => {
        if (!criteria) return 'No criteria specified';

        // If criteria is already a string, try to parse it if it looks like JSON
        let data = criteria;
        if (typeof criteria === 'string') {
            if (criteria.startsWith('{')) {
                try {
                    data = JSON.parse(criteria);
                } catch (e) {
                    return criteria; // Fallback to raw string
                }
            } else {
                return criteria;
            }
        }

        // Format based on alert type
        if (data.alertType === 'price_deviation' || data.parameter1 === 'deviation_percentage') {
            const op = data.operator === '>=' ? '≥' : data.operator === '<=' ? '≤' : data.operator;
            return `Price deviation ${op} ${data.value}%`;
        }

        if (data.field === 'pct_change') {
            const op = data.operator === '>=' ? '≥' : data.operator === '<=' ? '≤' : data.operator;
            return `Daily gain ${op} ${data.value}%`;
        }

        if (data.sources) {
            return `Monitoring ${data.sources.length} sources${data.mention_symbols_only ? ' for symbol mentions' : ''}`;
        }

        // Catch-all for other JSON structures
        return Object.entries(data)
            .filter(([key]) => !['alertType', 'parameter1', 'field'].includes(key))
            .map(([key, val]) => `${key}: ${typeof val === 'object' ? JSON.stringify(val) : val}`)
            .join(', ');
    };

    return (
        <div className="mt-4 flex flex-col gap-3 animate-in fade-in slide-in-from-bottom-3 duration-500">
            <div className="flex items-center justify-between px-1">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <AlertCircle size={12} strokeWidth={3} />
                    Active Monitoring ({alerts.length})
                </span>
            </div>

            <div className="grid gap-2.5">
                {alerts.map((alert, idx) => (
                    <div
                        key={alert.id || idx}
                        className="group relative bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-300 overflow-hidden cursor-default"
                    >
                        {/* Decorative background pulse */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/30 rounded-full blur-3xl -mr-12 -mt-12 group-hover:bg-blue-100/50 transition-colors duration-500" />

                        <div className="flex items-start justify-between relative z-10">
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5 p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors duration-300">
                                    {getCategoryIcon(alert.category)}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-sm font-bold text-gray-800 line-clamp-1">
                                            {alert.name || alert.title || `Alert #${alert.id}`}
                                        </h4>
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter shadow-sm border ${alert.status?.includes('Active') || alert.status === 'active'
                                                ? 'bg-green-500 text-white border-green-600'
                                                : 'bg-gray-100 text-gray-500 border-gray-200'
                                            }`}>
                                            {alert.status || 'Active'}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-0.5 font-medium flex items-center gap-1">
                                        {formatCriteria(alert.criteria)}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-400 transform group-hover:translate-x-0.5 transition-all mt-1" />
                        </div>

                        {/* ID Badge */}
                        <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between opacity-60 group-hover:opacity-100 transition-opacity">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1 text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                                    <Clock size={10} />
                                    ID: {alert.id}
                                </div>
                                <div className="w-1 h-1 rounded-full bg-gray-200" />
                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                                    {alert.category || 'System'}
                                </div>
                            </div>
                            {alert.status?.includes('Active') && (
                                <div className="flex items-center gap-1 text-[10px] text-green-600 font-black italic uppercase">
                                    Live <Check size={10} strokeWidth={4} />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AlertListArtifact;
